import { ROUTES } from "@nf/constants";
import { debounce } from "debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UrlObject } from "url";

import { Input } from "components";
import { useRouter } from "hooks";
import { useIsTabletDown } from "hooks/useIsTabletDown";

interface UseSearchInputLogicOptions {
  withExpand?: boolean;
}

export const useSearchInputLogic = ({
  withExpand = false,
}: UseSearchInputLogicOptions = {}) => {
  const { isTabletDown } = useIsTabletDown();
  const router = useRouter();
  const input = useRef<React.ElementRef<typeof Input>>(null);

  const query = router.query.query ?? "";
  const decodedQuery = decodeURIComponent(query);
  const [searchPhrase, setSearchPhrase] = useState(decodedQuery);

  const [isInputExpanded, setExpand] = useState(withExpand);

  useEffect(() => {
    if (withExpand && isInputExpanded) {
      const timeout = setTimeout(() => input.current?.focus(), 1000);
      return () => clearTimeout(timeout);
    } else {
      // focus immediately when rendered
      input.current?.focus();
    }
  }, []);

  const updateSearchQueryInUrl = useCallback((query?: string) => {
    const isOnSearchPage = router.asPath.includes(ROUTES.SEARCH_SCREEN);

    const url: UrlObject = {
      pathname: ROUTES.SEARCH_SCREEN,
    };

    if (query?.length) {
      url.query = {
        query: encodeURIComponent(query.trim()),
      };
    }

    if (isOnSearchPage) {
      router.replace(url, undefined, { shallow: true });
    } else {
      router.push(url);
    }
  }, []);

  const debouncedUpdateSearchQueryInUrl = useMemo(
    () => debounce(updateSearchQueryInUrl, 300),
    [updateSearchQueryInUrl]
  );

  const handleClear = () => {
    setSearchPhrase("");
    updateSearchQueryInUrl("");

    if (withExpand && !isTabletDown) {
      setExpand(false);
    }
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchPhrase(newValue);
      debouncedUpdateSearchQueryInUrl(newValue);
    },
    [debouncedUpdateSearchQueryInUrl]
  );

  return {
    inputRef: input,
    searchPhrase,
    handleClear,
    handleInputChange,

    ...(withExpand && {
      isInputExpanded,
      expandInput: () => setExpand(true),
      collapseInput: () => setExpand(false),
    }),
  };
};
